import { useState, useEffect, useMemo } from 'react'
import DashboardLayout from '../Layout/DashboardLayout'
import axios from 'axios'
import { BASE_URL } from '../apiConfig'
import Cookies from 'js-cookie'
import { toast } from 'sonner'
import { useTranslation } from '../LanguageContext'
import { useAuth } from '../AuthContext'
import Loading from '../element/Loading'
import { PlusCircle, SquarePen, Trash2, X } from 'lucide-react'

export default function ChargesPage() {
  const { t, lang } = useTranslation()
  const { can } = useAuth()
  const token = Cookies.get('WEB_SSR')

  const [charges, setCharges] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingCharge, setEditingCharge] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [appliedDateFrom, setAppliedDateFrom] = useState('')
  const [appliedDateTo, setAppliedDateTo] = useState('')

  const [form, setForm] = useState({
    title: '',
    amount: '',
    date: '',
    notes: '',
  })

  const headers = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }

  const fetchCharges = async (from, to) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (from) params.append('date_from', from)
      if (to) params.append('date_to', to)
      const response = await axios.get(`${BASE_URL}/api/charges?${params.toString()}`, { headers })
      if (response.data.success) {
        setCharges(response.data.data || [])
      }
    } catch (error) {
      console.error(error)
      toast.error(t('charges.error.loading'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchCharges(appliedDateFrom, appliedDateTo) }, [appliedDateFrom, appliedDateTo])

  useEffect(() => { fetchCharges() }, [])

  const totalAmount = useMemo(() => {
    return charges.reduce((sum, c) => sum + Number(c.amount), 0)
  }, [charges])

  const openAddModal = () => {
    setEditingCharge(null)
    setForm({ title: '', amount: '', date: '', notes: '' })
    setModalOpen(true)
  }

  const openEditModal = (charge) => {
    setEditingCharge(charge)
    setForm({
      title: charge.title,
      amount: charge.amount,
      date: charge.date,
      notes: charge.notes || '',
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    if (!form.title || !form.amount) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      if (editingCharge) {
        const response = await axios.put(`${BASE_URL}/api/charges/${editingCharge.id}`, form, { headers })
        if (response.data.success) {
          toast.success(t('charges.update_success'))
          fetchCharges(appliedDateFrom, appliedDateTo)
          setModalOpen(false)
        }
      } else {
        const response = await axios.post(`${BASE_URL}/api/charges`, form, { headers })
        if (response.data.success) {
          toast.success(t('charges.add_success'))
          fetchCharges(appliedDateFrom, appliedDateTo)
          setModalOpen(false)
        }
      }
    } catch (error) {
      console.error(error)
      toast.error(t('charges.error.saving'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    setDeleting(true)
    try {
      const response = await axios.delete(`${BASE_URL}/api/charges/${id}`, { headers })
      if (response.data.success) {
        toast.success(t('charges.delete_success'))
        fetchCharges(appliedDateFrom, appliedDateTo)
        setDeleteConfirm(null)
      }
    } catch (error) {
      console.error(error)
      toast.error(t('charges.error.deleting'))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl max-lg:text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">{t('charges.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('charges.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900 dark:bg-gray-800 dark:text-gray-100">
              {t('charges.total')}: {Number(totalAmount).toFixed(2)}
            </div>
            {can('charges.create') && (
              <button
                type="button"
                onClick={openAddModal}
                className="inline-flex items-center gap-2 rounded-lg bg-[#171717] px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
              >
                <PlusCircle className="w-4 h-4" />
                {t('charges.add')}
              </button>
            )}
          </div>
        </div>

        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('charges.from')}</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('charges.to')}</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>
          <button
            type="button"
            onClick={() => { setAppliedDateFrom(dateFrom); setAppliedDateTo(dateTo) }}
            className="rounded-lg bg-[#171717] px-4 py-2 text-xs font-semibold text-white transition hover:bg-gray-800"
          >
            {t('charges.filter_btn')}
          </button>
          <button
            type="button"
            onClick={() => { setDateFrom(''); setDateTo(''); setAppliedDateFrom(''); setAppliedDateTo('') }}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {t('charges.clear')}
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-2">
              <Loading size={10} />  
            </div>
          ) : charges.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400">{t('charges.no_charges')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-gray-900 dark:text-gray-100">
                <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3">{t('charges.title_label')}</th>
                    <th className="px-4 py-3">{t('charges.amount')}</th>
                    <th className="px-4 py-3">{t('charges.date')}</th>
                    <th className="px-4 py-3">{t('charges.notes')}</th>
                    <th className="px-4 py-3 text-right">{t('charges.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((charge) => (
                    <tr key={charge.id} className="border-b border-gray-200 dark:border-gray-700">
                      <td className="px-4 py-3 align-top font-medium">{charge.title}</td>
                      <td className="px-4 py-3 align-top">{Number(charge.amount).toFixed(2)}</td>
                      <td className="px-4 py-3 align-top">{new Date(charge.date).toLocaleDateString('en-GB')}</td>
                      <td className="px-4 py-3 align-top text-gray-500 max-w-xs truncate">{charge.notes || '—'}</td>
                      <td className="px-4 py-3 align-top text-right">
                        <div className="flex items-center justify-end gap-2">
                          {can('charges.edit') && (
                            <button
                              type="button"
                              onClick={() => openEditModal(charge)}
                              className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                            >
                              <SquarePen className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {can('charges.delete') && (
                            <button
                              type="button"
                              onClick={() => setDeleteConfirm(charge)}
                              className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setModalOpen(false)}>
          <div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                {editingCharge ? t('charges.edit') : t('charges.add')}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                <span>{t('charges.title_label')}</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  placeholder={t('charges.title_placeholder')}
                />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block text-sm text-gray-700 dark:text-gray-300">
                  <span>{t('charges.amount')}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    placeholder={t('charges.amount_placeholder')}
                  />
                </label>
                {editingCharge && (
                  <label className="block text-sm text-gray-700 dark:text-gray-300">
                    <span>{t('charges.date')}</span>
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    />
                  </label>
                )}
              </div>
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                <span>{t('charges.notes')}</span>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 resize-none"
                  placeholder={t('charges.notes_placeholder')}
                />
              </label>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="w-full rounded-lg bg-[#171717] px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {saving ? t('charges.saving') : (editingCharge ? t('charges.edit') : t('charges.add'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirm(null)}>
          <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('charges.delete_confirm')}</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{deleteConfirm.title} — {Number(deleteConfirm.amount).toFixed(2)}</p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('products.delete.cancel')}
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={deleting}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-70"
              >
                {deleting ? t('products.delete.deleting') : t('products.delete.confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}
